//
// ISOMORPHIC — this file is byte-identical to
// client/src/cognitive-flows/components/canvas/numberFormat.js, and
// tests/unit/server/cognitive-flows/canvasNumberFormatParity.test.js fails the
// moment the two drift. No imports, no Node or DOM APIs.
//
// Thousands separators for canvas figures a reader scans: table cells and the
// numbers inside an agent's callout prose. Both are DISPLAY-only — stored
// results never change. The rule everywhere is "group a real quantity, leave an
// identifier alone": a year, an id, a zip or a phone number must not become
// "2,025" or "94,105".

// Headers whose numbers are labels, not quantities. Matched on whole words after
// camelCase / snake_case are split ("campaignId" -> "campaign Id").
const LABEL_HEADER_WORDS = /\b(id|ids|year|yr|zip|zipcode|postal|postcode|pincode|phone|mobile|tel|fax|sku|code|ref|gtin|upc|ean)\b/i;
const PERCENT_HEADER = /%|percent|pct/i;

const isYear = (n) => Number.isInteger(n) && n >= 1900 && n <= 2100;

/** True when a column header says its numbers are ids / years / zips / percents. */
export function isLabelHeader(header) {
    const h = String(header ?? "");
    if (PERCENT_HEADER.test(h)) return true;
    const words = h.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/[_\-.]+/g, " ");
    return LABEL_HEADER_WORDS.test(words);
}

/** "1234567.89" -> "1,234,567.89" (digits only in, no exponent). */
function groupPlain(abs) {
    const [int, frac] = abs.split(".");
    return int.replace(/\B(?=(\d{3})+(?!\d))/g, ",") + (frac ? `.${frac}` : "");
}

/**
 * A table cell as grouped text, or null when the cell should print as it is
 * (not a number, below 1,000, a year, an id-like header, a percent, exponent
 * notation). `opts.strings` also accepts a plain numeric string ("108266"),
 * for the email, whose cells can arrive as strings; a leading zero ("02134")
 * is a zip-like label and is left alone.
 */
export function groupedCell(v, header, opts) {
    let n;
    if (typeof v === "number") n = v;
    else if (opts && opts.strings && typeof v === "string" && /^-?[1-9]\d*(\.\d+)?$/.test(v.trim())) n = Number(v.trim());
    else return null;
    if (!Number.isFinite(n) || Math.abs(n) < 1000 || isYear(n) || isLabelHeader(header)) return null;
    const abs = String(Math.abs(n));
    if (/e/i.test(abs)) return null;
    return `${n < 0 ? "-" : ""}${groupPlain(abs)}`;
}

// Spans left untouched: fenced / inline code and URLs.
const PROTECTED = /(```[\s\S]*?```|`[^`\n]*`|(?:https?:\/\/|www\.)\S+)/;
// A 4+ digit integer run, not glued to a letter, a digit group, a decimal's
// fraction or a dash / slash chain (ids, phone numbers, dates, paths).
const INT_RUN = /(?<![\w.,/\-])(\d{4,})(\.\d+)?(?![\w])/g;

/** Group the 4+ digit integers in prose ("108266 clicks" -> "108,266 clicks"). */
export function groupNumbersInText(text) {
    if (typeof text !== "string" || text.length < 4) return text;
    return text.split(PROTECTED).map((part, i) => {
        if (i % 2 === 1) return part;
        return part.replace(INT_RUN, (m, int, frac) => {
            if (!frac && isYear(Number(int))) return m;
            return groupPlain(int) + (frac || "");
        });
    }).join("");
}
